import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { usersService, User } from '@/services/users.service';
import toast from 'react-hot-toast';

interface ReassignModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentAssignee?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  onReassign: (userId: string) => Promise<void>;
}

export default function ReassignModal({
  isOpen,
  onClose,
  currentAssignee,
  onReassign,
}: ReassignModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Search users when query changes
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.trim().length < 2) {
        setUsers([]);
        return;
      }

      try {
        setIsSearching(true);
        const results = await usersService.searchUsers(searchQuery, 10);
        setUsers(results);
      } catch (error) {
        console.error('Failed to search users:', error);
        toast.error('Failed to search users');
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setUsers([]);
      setSelectedUser(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!selectedUser) {return;}

    try {
      setIsSubmitting(true);
      await onReassign(selectedUser.id);
      toast.success(`Incident reassigned to ${selectedUser.fullName}`);
      onClose();
    } catch (error) {
      console.error('Failed to reassign incident:', error);
      toast.error('Failed to reassign incident');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pt-16 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-md pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                  Reassign Incident
                </h2>
                <button
                  onClick={onClose}
                  className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Current Assignee */}
                {currentAssignee && (
                  <div className="p-3 bg-secondary-50 dark:bg-secondary-900/50 rounded-lg">
                    <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">
                      Currently assigned to:
                    </p>
                    <p className="font-medium text-secondary-900 dark:text-white">
                      {currentAssignee.firstName} {currentAssignee.lastName}
                    </p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      {currentAssignee.email}
                    </p>
                  </div>
                )}

                {/* Search Input */}
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                  <Input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>

                {/* Search Results */}
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {isSearching && (
                    <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                      <p className="mt-2 text-sm">Searching...</p>
                    </div>
                  )}

                  {!isSearching && searchQuery.trim().length > 0 && users.length === 0 && (
                    <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                      <UserIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No users found</p>
                    </div>
                  )}

                  {!isSearching && searchQuery.trim().length < 2 && (
                    <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                      <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Type at least 2 characters to search</p>
                    </div>
                  )}

                  {users.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        selectedUser?.id === user.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300 dark:hover:border-secondary-600'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <UserIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-secondary-900 dark:text-white truncate">
                            {user.fullName}
                          </p>
                          <p className="text-sm text-secondary-500 dark:text-secondary-400 truncate">
                            {user.email}
                          </p>
                          {user.roles.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {user.roles.map((role) => (
                                <span
                                  key={role.id}
                                  className="text-xs px-2 py-0.5 bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 rounded"
                                >
                                  {usersService.getRoleDisplayName(role)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {selectedUser?.id === user.id && (
                          <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200 dark:border-secondary-700">
                <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!selectedUser || isSubmitting}
                  className="min-w-24"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    'Reassign'
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
